// src/components/Step1ProjectInfo.jsx
import React, { useEffect, useState } from "react";
import { api as axios, authFetch } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";
import useAutoResizeTextareas from "../utils/useAutoResizeTextareas";
import { ROUTES } from "../config/routes";
import { printProjectReport } from "../utils/printProjectReport";
import RichTextEditor from "./RichTextEditor";

// The theme's cost-estimation.html mockup shows these seven sections as
// tabs within one page. This app instead gives each one its own route
// (Step1..Step5, BasisOfCost, ElementScopePage1) with real Next/Back
// buttons that save to the backend as you go -- Step2+ expect
// localStorage("project_db_id") to already exist from Step1's save, for
// instance. So unlike the theme's tabs, only "Project" (this page) is a
// real, clickable step here; the rest are shown for visual parity with
// the mockup's step indicator but are inert -- wiring them up to jump
// straight into the middle of the wizard would let someone skip the
// save that step depends on.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "project";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// FIX: was hardcoded to "/api" -- every other page
// derives this from API_ORIGIN (src/config/apiConfig.js) so switching
// hosts/HTTPS is a one-file change; this page had been missed.
const API_BASE = API_ORIGIN;

// Local YYYY-MM-DD for "today", built from date parts rather than
// `new Date().toISOString()` (which is UTC and can read as yesterday
// or tomorrow depending on the browser's timezone). The Date field
// below is disabled/read-only and always shows this -- per request,
// the estimation date isn't user-editable, same as "Estimated by" is
// always the logged-in user rather than a free-text field.
function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Maps each of the 7 wizard steps to its brand-new, read-only "view"
// route (COST_ESTIMATION_STEP1_VIEW etc., src/config/routes.js) so the
// step tabs below can jump between read-only pages for the same saved
// project without ever landing on the real, editable Step1..Step5 /
// BasisOfCost / ElementScopePage1 routes.
const STEP_VIEW_ROUTES = {
  project: ROUTES.COST_ESTIMATION_STEP1_VIEW,
  budget: ROUTES.COST_ESTIMATION_STEP2_VIEW,
  asset: ROUTES.COST_ESTIMATION_STEP3_VIEW,
  element: ROUTES.COST_ESTIMATION_STEP4_VIEW,
  estimation: ROUTES.COST_ESTIMATION_STEP5_VIEW,
  basis: ROUTES.BASIS_OF_COST_VIEW,
  construction: ROUTES.ELEMENT_SCOPE_PAGE1_VIEW,
};

export default function Step1ProjectInfoReadOnly() {
  useAutoResizeTextareas();
  const navigate = useNavigate();

  // The logged-in user, cached in localStorage("currentUser") right
  // after /verify-otp (see OtpVerify.jsx) as
  // {id, email, UserName, RoleID, RoleName}. "Estimated by" is locked
  // to this person -- it's no longer a dropdown listing every user in
  // the system, it just shows whoever is actually logged in right now.
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
  const estimatedByName = currentUser?.UserName || currentUser?.email || "Not logged in";
  const estimatedByEmail = currentUser?.email || localStorage.getItem("user_email") || "";

  const [opcos, setOpcos] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  // "Proposed Doc" -- same field as the real Step1ProjectInfo.jsx (see
  // that file for the full read/write flow), but this read-only view
  // only ever shows a VIEW link -- there's no upload control here since
  // nothing on this page is editable (everything below is wrapped in a
  // disabled <fieldset>).
  const [proposedDoc, setProposedDoc] = useState({ filename: "", version: 0 });
  // Styled in-page error banner (see the render below, right under the
  // wizard tabs) -- replaces the native browser alert() this page used
  // to call for every validation/server failure, same "alert alert-danger"
  // pattern Step5Final.jsx already uses for its own save errors.
  const [serverError, setServerError] = useState(null);
  // The project ID this project was actually saved under, captured only
  // when this page loaded an existing project (via ProjectList.jsx's Edit
  // action, which sets localStorage "project_db_id"). Sent back to the
  // backend as original_projectid so it can tell "re-saving project X
  // under its own ID" (fine) apart from "a new/renamed project wants an
  // ID that's already taken by someone else" (blocked) -- see goNext().
  const [originalProjectID, setOriginalProjectID] = useState("");

  const [form, setForm] = useState({
    projectName: "",
    projectID: "",
    // Per request: the Project List's "Estimated By" column should show
    // the person's NAME, not their email address. estimatedByEmail is
    // still captured separately as `created_by` in goNext()'s payload
    // below, so nothing loses track of the real email.
    estimatedBy: estimatedByName,
    date: todayISO(),
    opco: "",
    class: "",
    C_ID: "",
    intro: "",
    inflation: "",
    financial: "",
    contingency: "",
    risks: "",
    opportunities: "",
  });

  useEffect(() => {
    axios.get(`${API_BASE}/opco`).then((res) => setOpcos(res.data.data || []));
    axios.get(`${API_BASE}/class`).then((res) => setClasses(res.data.data || []));

    const saved = JSON.parse(localStorage.getItem("step1"));
    const currentProjectID = localStorage.getItem("projectID");
    // FIX, per request ("read only report not showing all data"): "step1"
    // only ever gets (re)written by this page's own save, or by
    // ProjectList.jsx's Edit/View actions. Landing here some other way --
    // clicking the "Project" wizard-step tab from another already-loaded
    // read-only page, a fresh tab/session, or the browser having cleared
    // localStorage -- can leave "step1" completely missing (or missing
    // its projectName) even though localStorage("projectID") is still
    // set. That showed every field blank and the breadcrumb falling back
    // to "Start Cost Estimation" instead of the real project. Same class
    // of bug already fixed for the Asset/Element read-only pages this
    // session: fall back to fetching the project straight from the
    // backend by its Project ID (the same GET /projects the Project List
    // itself uses, filtered by ?search=<projectID>) instead of showing a
    // blank page.
    //
    // FIX, per report (Dashboard's Executive Summary "Project" tile
    // opening the WRONG project): the opposite kind of staleness. "step1"
    // isn't cleared when navigating here for a DIFFERENT project (e.g.
    // DashboardExecutiveSummary.jsx's goToStep only ever writes
    // "projectID"/"project_db_id", never touches "step1") -- so this used
    // to trust whatever "step1" was left over from the LAST project
    // actually opened through the normal edit/view wizard, even though
    // "projectID" (set moments ago by the click that navigated here) named
    // a completely different project. That's why picking "Project
    // Timeline and Cost Estimate Structure Breakdown" on the dashboard and
    // clicking through landed on "Emergency Access Road at Bisha Airport"
    // instead -- whichever project's Step 1 had most recently been viewed
    // that session. Requiring saved.projectID to actually match
    // currentProjectID (when one is set) before trusting the cached draft
    // treats a mismatched leftover the same as a missing one: fetch fresh
    // from the backend instead of showing someone else's project.
    if (saved && saved.projectName && (!currentProjectID || String(saved.projectID) === String(currentProjectID))) {
      setForm({
        ...saved,
        // Resuming a draft or editing an existing project: keep
        // whatever date was saved -- past or future -- instead of
        // overwriting it with today. Only fall back to today if that
        // draft somehow has no date at all.
        date: saved.date || todayISO(),
        // Always the person currently logged in, even if the draft (or
        // the project being edited) was originally saved under someone
        // else -- "Estimated by" reflects who's at the keyboard now.
        estimatedBy: estimatedByName,
      });

      // "project_db_id" is only ever set for a project that's actually
      // been saved (ProjectList.jsx's Edit action, or this same page's
      // own goNext() after a successful save) -- a brand-new, not-yet-
      // saved draft never has it. Only in that case is saved.projectID a
      // real, already-taken ID this save is allowed to reuse.
      if (localStorage.getItem("project_db_id")) {
        setOriginalProjectID(saved.projectID || "");
      }
      return;
    }

    const projectID = currentProjectID;
    if (!projectID) return;

    axios
      .get(`${API_BASE}/projects`, { params: { search: projectID, pageSize: 20 } })
      .then((res) => {
        const rows = res.data?.data || [];
        const row =
          rows.find((r) => String(r.projectid) === String(projectID)) || rows[0];
        if (!row) return;

        const rebuilt = {
          projectName: row.project_name || "",
          projectID: row.projectid || "",
          estimatedBy: estimatedByName,
          date: row.estimation_date ? String(row.estimation_date).substring(0, 10) : todayISO(),
          opco: row.opco_id != null ? String(row.opco_id) : "",
          class: row.class || "",
          C_ID: row.c_id || "",
          intro: row.intro || "",
          inflation: row.inflation || "",
          financial: row.financial || "",
          contingency: row.contingency || "",
          risks: row.risks || "",
          opportunities: row.opportunities || "",
        };
        setForm(rebuilt);
        // Backfill "step1" (and the flat keys other pages/read-only steps
        // read) so the rest of this session doesn't have to hit the
        // backend again -- same shape ProjectList.jsx's handleView already
        // writes.
        localStorage.setItem("step1", JSON.stringify(rebuilt));
        localStorage.setItem("projectName", rebuilt.projectName);
        setOriginalProjectID(rebuilt.projectID);
      })
      .catch((err) => console.error("Failed to load project info", err));
  }, []);

  // Loads the Proposed Doc uploaded from the real (editable)
  // Step1ProjectInfo.jsx, if any -- same GET /projects/proposed_doc call
  // that page makes, keyed off the same originalProjectID this page's
  // own mount effect above already resolves.
  useEffect(() => {
    if (!originalProjectID) return;
    axios
      .get(`${API_BASE}/projects/proposed_doc`, { params: { Projectid: originalProjectID } })
      .then((res) => {
        if (res.data?.success && res.data?.found) {
          setProposedDoc({ filename: res.data.filename, version: res.data.version });
        }
      })
      .catch((err) => console.error("Failed to load proposed document", err));
  }, [originalProjectID]);

  // Same authenticated-blob-to-new-tab pattern as the real
  // Step1ProjectInfo.jsx's viewProposedDoc() / BasisOfCostReadOnly.jsx's
  // viewExistingBasisFile() -- a plain <a href> can't carry the app's
  // JWT bearer token.
  const viewProposedDoc = async () => {
    if (!proposedDoc.filename || !originalProjectID) return;
    try {
      const res = await axios.get(`${API_BASE}/projects/proposed_doc/file`, {
        params: { Projectid: originalProjectID, filename: proposedDoc.filename },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to open proposed document", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "class") {
      let cid = "";
      if (value === "Class 3") cid = "C3";
      if (value === "Class 4") cid = "C4";
      if (value === "Class 5") cid = "C5";
      // FIX: was setForm({ ...form, ... }), reading the `form` variable
      // captured in this closure rather than the freshest state. The Date
      // field (native <input type="date">) can fire its change event in a
      // way that lands after another render already queued from the same
      // interaction, so that stale `form` would win and silently drop the
      // just-picked date back to whatever it was before. The functional
      // updater always applies on top of the latest state instead of a
      // possibly-stale snapshot.
      setForm((prev) => ({ ...prev, class: value, C_ID: cid }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const goNext = async () => {
    setServerError(null);
    if (!form.projectName.trim()) return setServerError("Project Name cannot be blank.");
    if (!form.projectID.trim()) return setServerError("Project ID cannot be blank.");
    if (!form.class.trim()) return setServerError("Class cannot be blank.");
    if (!form.C_ID.trim()) return setServerError("C_ID missing. Select Class again.");

    const payload = {
      project_name: form.projectName,
      projectid: form.projectID,
      // Only a real, already-taken ID (see the mount effect above) is
      // sent here -- lets the backend allow re-saving this exact project
      // under its own unchanged ID while still blocking a new/renamed
      // project from reusing someone else's ID.
      original_projectid: originalProjectID || null,
      estimated_by: form.estimatedBy,
      estimation_date: form.date || null,
      opco_id: form.opco || null,
      class: form.class,
      c_id: form.C_ID,
      intro: form.intro,
      inflation: form.inflation,
      financial: form.financial,
      contingency: form.contingency,
      risks: form.risks,
      opportunities: form.opportunities,
      status: "P",
      // NOTE: was `|| form.estimatedBy` as the last fallback, but that
      // field now holds the display NAME (see estimatedBy above), not an
      // email -- fall back to estimatedByEmail instead so this never
      // ends up with a name where an email is expected.
      created_by: localStorage.getItem("user_email") || estimatedByEmail || null,
    };

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/projects`, payload);

      if (res.data?.success && res.data?.data?.id) {
        const projectDbId = res.data.data.id;

        localStorage.setItem("projectID", form.projectID);
        localStorage.setItem("projectName", form.projectName);
        localStorage.setItem("estimatedBy", form.estimatedBy);
        localStorage.setItem("estimationDate", form.date);
        localStorage.setItem("operatingCompany", form.opco);

        localStorage.setItem("intro", form.intro);
        localStorage.setItem("inflation", form.inflation);
        localStorage.setItem("financial", form.financial);
        localStorage.setItem("contingency", form.contingency);
        localStorage.setItem("risks", form.risks);
        localStorage.setItem("opportunities", form.opportunities);

        localStorage.setItem("class", form.class);
        localStorage.setItem("C_ID", form.C_ID);

        localStorage.setItem("project_db_id", projectDbId);
        localStorage.setItem("step1", JSON.stringify(form));

        navigate(ROUTES.COST_ESTIMATION_STEP2);
      } else {
        setServerError(res.data?.message || "Failed to create project.");
      }
    } catch (err) {
      console.error(err);
      // axios throws on any non-2xx response, so every validation failure
      // from the backend (duplicate Project ID, missing fields, etc.)
      // lands here -- surface ITS actual message instead of a generic
      // "Server error" for all of them alike.
      const backendMessage = err.response?.data?.message || err.response?.data?.error;
      setServerError(backendMessage || "Server error while creating project. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Per request: once a project has been through the full wizard and
  // Finish has been clicked, every read-only step page for it gets a
  // "Print Report" / "View Dashboard" shortcut in the top-right corner,
  // below the breadcrumb -- no need to jump back to the Estimation
  // Summary page just to print the report or check the portfolio
  // Dashboard. Print Report reuses printProjectReport.js's self-contained
  // generator (the same one ProjectList.jsx's "Estimated" column icon
  // already calls) -- given just a projectId, it fetches everything
  // itself and opens the report exactly the same way.
  const [printReportLoading, setPrintReportLoading] = useState(false);
  const handlePrintReport = async () => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;
    setPrintReportLoading(true);
    try {
      await printProjectReport({ authFetch, API_BASE, projectId });
    } catch (err) {
      console.error("Failed to generate report", err);
    } finally {
      setPrintReportLoading(false);
    }
  };

  return (
    <>
      {/* Project name in a white block, matching every other step in this
          wizard, bound to form.projectName (the field below) so it updates
          live as the user types. This is the one page where projectName
          can still be genuinely empty (a brand-new estimation, before
          anything's been typed) -- every other wizard step only renders
          once Step1 has already set it. So only here, an empty name falls
          back to the original "Start Cost Estimation" title instead of
          "Project : —". */}
      <div className="page-title-head d-flex align-items-center flex-wrap gap-2">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail (same pattern
              as Step2RiskInflation.jsx onward): "Cost Estimation /
              Project / Project : {name}" (or "Start Cost Estimation"
              before a name is set, same fallback this page always had).
              Non-current segments + "/" separators use #02bc9c blended
              90% toward white (a light mint tint, matching the
              reference design's muted breadcrumb segments); the current
              segment stays the full #02bc9c, bold, so it reads as the
              active item -- matching the reference's blue "Portfolio"
              segment. Replaces the earlier #788ca5 bordered/uppercase
              title treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {form.projectName ? form.projectName : "Start Cost Estimation"}
            </span>
          </nav>
        </div>

        {/* Per request: "Print Report" / "View Dashboard" shortcut, top
            right corner below the header, same on every read-only wizard
            step page for a finished project. */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm text-white px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#124d52", borderRadius: 2 }}
            onClick={handlePrintReport}
            disabled={printReportLoading}
          >
            <i className="ti ti-printer fs-5" />
            {printReportLoading ? "Preparing..." : "Print Report"}
          </button>
          <button
            type="button"
            className="btn btn-sm px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#02bc9c", color: "#fff", borderRadius: 2 }}
            onClick={() => navigate(ROUTES.DASHBOARD)}
          >
            <i className="ti ti-dashboard fs-5" />
            View Dashboard
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {/* Per request, this page's card background is explicitly
              #FFFFFF -- scoped to just this page via an inline style
              rather than a shared class, so no other page's card is
              affected. */}
          <div className="card" style={{ backgroundColor: "#FFFFFF" }}>
            <div className="card-body">

              {/* Wizard progress bar -- theme's cost-estimation.html has a
                  thin blue bar (.progress > .progress-bar.bg-primary) above
                  the step tabs, driven there by its own single-page tab JS
                  (data-wizard-progress). This app has no such JS since each
                  step is its own route, so the width is just a fixed
                  percentage per page: (this step's position / 7 steps). */}
              <div className="progress mb-4" style={{ height: 6 }}>
                {/* wizard-progress-bar (not bg-primary) -- per request, this
                    bar is brand green (#0f4c42, themeOverrides.css) on all
                    7 wizard stages, not the theme's default blue. Scoped to
                    its own class so the shared bg-primary utility used
                    elsewhere in the app is untouched. On this read-only
                    view every step is treated as "done" (this is a saved
                    project's data), so the bar is always full. */}
                <div className="progress-bar wizard-progress-bar" style={{ width: "100%" }} />
              </div>

              {/* Step indicator -- unlike the real Step1ProjectInfo.jsx
                  (where only "Project" is a real, clickable tab -- see
                  the note that used to live above WIZARD_STEPS), every
                  tab here is clickable: this is a saved/finished project,
                  so every step is "done" and jumps to that step's own
                  read-only view (STEP_VIEW_ROUTES above), never to the
                  real editable Step1..Step5 / BasisOfCost /
                  ElementScopePage1 pages. */}
              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className="nav-link h-100 wizard-item-done"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(STEP_VIEW_ROUTES[step.key])}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(STEP_VIEW_ROUTES[step.key]);
                        }
                      }}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              {/* Per request, darkened to full opacity -- Bootstrap's
                  default <hr> here was too faint at its default 0.25. */}
              <hr className="mt-3" style={{ opacity: 1 }} />

              {serverError && (
                <div className="alert alert-danger" role="alert">
                  {serverError}
                </div>
              )}

              {/* Everything from here down is the real Step1ProjectInfo.jsx
                  form, unchanged except for this wrapping <fieldset disabled>.
                  A native disabled fieldset automatically disables every
                  <input>/<select>/<textarea>/<button> inside it, so all the
                  fields below are inert without hand-editing each one. */}
              <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
              {/* ===================== PROJECT INFORMATION ===================== */}
              <h5 className="mb-3 text-green fs-4 fw-bold">Project Information</h5>
              {/* Per request, this whole page is now plain white -- the
                  "bg-disabled" class removed here was the last gray tint
                  on the page: it painted this row's own panel gray AND
                  (via app.min.css's ".bg-disabled input, .bg-disabled
                  select" rule) forced every field inside it -- Project
                  Name/ID, Estimated by, Date, OpCo, Class --
                  to that same gray background regardless of their own
                  className. Removing it lets all of them fall back to
                  form-control/form-select's normal white. */}
              <div className="row pt-3">
                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Project Name</label>
                    <input
                      type="text"
                      name="projectName"
                      className="form-control"
                      placeholder="Enter project name"
                      value={form.projectName}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Project ID</label>
                    <input
                      type="text"
                      name="projectID"
                      className="form-control"
                      value={form.projectID}
                      onChange={handleChange}
                      // Per request: once a project has actually been
                      // saved (originalProjectID is only ever set when
                      // this page loaded an existing project -- see the
                      // mount effect above), its Project ID can no longer
                      // be changed from here -- Project Name stays
                      // editable, this field alone locks. A brand-new,
                      // not-yet-saved draft still needs to type one in,
                      // so it stays editable until the first save.
                      // FIX, per request: uses readOnly alone now (not
                      // disabled) so the field keeps a plain white
                      // background instead of Bootstrap's grayed-out
                      // disabled look -- still not editable, still
                      // selectable/copyable, just no longer tinted.
                      readOnly={Boolean(originalProjectID)}
                      title={originalProjectID ? "Project ID can't be changed after the project is created." : undefined}
                    />
                  </div>
                </div>

                {/* Per explicit request, replaces "Estimated by" -- this
                    read-only page only ever shows a VIEW link (no upload
                    control; that lives on the real, editable
                    Step1ProjectInfo.jsx). Still sits inside the same
                    disabled <fieldset> as everything else on this page,
                    but a native <fieldset disabled> only disables actual
                    form controls (input/select/textarea/button) -- a
                    plain <a> stays clickable, which is exactly what's
                    wanted here since viewing is a read-only action. */}
                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Proposed Doc</label>
                    <div>
                      {proposedDoc.filename ? (
                        <a
                          href="#"
                          className="fw-semibold"
                          style={{ color: "#02bc9c" }}
                          onClick={(e) => {
                            e.preventDefault();
                            viewProposedDoc();
                          }}
                        >
                          VIEW
                        </a>
                      ) : (
                        <span className="form-control text-muted">No document uploaded</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Per request, reordered to OpCo Title -> Class -> Date
                    (was Date -> OpCo Title -> Class) -- kept in sync with
                    the same reorder on the real Step1ProjectInfo.jsx. */}
                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">OpCo Title</label>
                    <select
                      name="opco"
                      className="form-select"
                      value={form.opco}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {opcos.map((o) => (
                        <option key={o.OpCo_ID} value={o.OpCo_ID}>
                          {o.OpCo_Name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">
                      Class <span className="text-muted fs-xxs"></span>
                    </label>
                    <select
                      name="class"
                      className="form-select"
                      value={form.class}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {classes.map((c) => (
                        <option key={c.C_ID} value={c.Class}>
                          {c.Class}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="mb-3">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      name="date"
                      className="form-control"
                      value={form.date}
                      onChange={handleChange}
                      // Per request, reverted back to disabled (not just
                      // readOnly) so this renders as a grayed-out field,
                      // matching the same change on the real
                      // Step1ProjectInfo.jsx (this page is also wrapped in
                      // a disabled <fieldset>, so it already rendered gray
                      // here -- kept in sync for source consistency).
                      disabled
                      readOnly
                      title="Date is always today's date"
                    />
                  </div>
                </div>
              </div>

              {/* ===================== PROJECT OVERVIEW ===================== */}
              <h5 className="mb-3 mt-2 text-green fs-4 fw-bold">Project Overview</h5>
              <hr className="mt-0 mb-3" style={{ opacity: 1 }} />
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Project Introduction</label>
                    {/* Matches the Rich Text field on Step1ProjectInfo.jsx
                        (form.intro is now an HTML string) -- explicitly
                        disabled=true here since a native <fieldset
                        disabled> (wrapping this whole page) does NOT
                        reach into a contentEditable div the way it does
                        a real <textarea>. */}
                    <RichTextEditor
                      name="intro"
                      placeholder="Enter project introduction"
                      value={form.intro}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Inflation Forecast</label>
                    <RichTextEditor
                      name="inflation"
                      placeholder="Enter inflation forecast"
                      value={form.inflation}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Financial Overview</label>
                    <RichTextEditor
                      name="financial"
                      placeholder="Enter financial overview"
                      value={form.financial}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Contingency Summary</label>
                    <RichTextEditor
                      name="contingency"
                      placeholder="Enter contingency summary"
                      value={form.contingency}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Risks</label>
                    <RichTextEditor
                      name="risks"
                      placeholder="Enter risks"
                      value={form.risks}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3">
                    <label className="form-label">Opportunities</label>
                    <RichTextEditor
                      name="opportunities"
                      placeholder="Enter opportunities"
                      value={form.opportunities}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                </div>
              </div>

              </fieldset>

              {/* No Save/Next/Back-to-wizard actions here -- this is a
                  read-only view of a finished project, opened from
                  EstimationSummary.jsx's step tabs. The only navigation
                  is back to that summary. */}
              <div className="d-flex justify-content-between mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(ROUTES.DASHBOARD, { state: { tab: "executive" } })}
                >
                  &larr; Back to Dashboard
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(ROUTES.COST_ESTIMATION_SUMMARY)}
                >
                  &larr; Back to Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}